import React, { useEffect, useState } from 'react';
import { SortType, Input, Table, Button, DOMHelper, Stack, IconButton, Tag, Divider, SelectPicker, Checkbox } from 'rsuite';
import EditIcon from '@rsuite/icons/legacy/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import { getCitiesLookup } from '@/services/city.service';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import ImageCell from '@/components/ImageCell';
import { RunEventList, RunEventPaginationFilterRequest, RunEventStatusList, RunEventType, RunEventTypeList } from '@/models/run-event.model';
import { getPagedRunEvents } from '@/services/run-event.service';
import { jsDateToZone } from '@/utils/date-time';
import PageContent from '@/components/PageContent';
import { getCountriesLookup } from '@/services/country.service';
import { getRunEventKeysLookup } from '@/services/run-event-key.service';
import { RunEventDistanceTypeList } from '@/models/run-event-distance.model';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const RunEventListPage = () => {

  const [filterReq, setFilterReq] = useState<RunEventPaginationFilterRequest>(new RunEventPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<RunEventList[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  const nav = useNavigate();
  
  useEffect(() => {
    const onlyStartedDistances = searchParams.get('only-started-distances');
    const type = RunEventType [searchParams.get('type') as keyof typeof RunEventType];
    setFilterReq(pre => ({
      ...pre,
      onlyStartedDistances: onlyStartedDistances === 'true',
      type: type
    }));
  }, []);

  useEffect(() => {
    fetchData(filterReq);
    updateSearchParams();
  }, [filterReq]);
  
  const updateSearchParams = () => {
    if(filterReq.onlyStartedDistances){ 
      searchParams.set('only-started-distances', 'true' );
    }else{
      searchParams.delete('only-started-distances');
    }
    if(filterReq.type){ 
      searchParams.set('type', RunEventType[filterReq.type]);
    }else{
      searchParams.delete('type');
    }
    setSearchParams(searchParams.toString());
  };

  const fetchData = async (req: RunEventPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<RunEventList> = await getPagedRunEvents(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching RunEvents:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Button appearance="primary" onClick={() => nav(`/run-events/new`)}>
          Add Run Event
        </Button>
        <Stack spacing={6}>
          <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, filterReq.countryId, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />
          <FetchingSelect
            label="Run Event Key"
            fetchData={(text, page) => getRunEventKeysLookup(text, filterReq.cityId, page)}
            value={filterReq.runEventKeyId}
            onChange={dataKey => setFilterReq(pre => ({...pre, runEventKeyId: dataKey}))}
          />
          <SelectPicker
            label="Status"
            data={RunEventStatusList}
            searchable={false}
            value={filterReq.status ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, status: dataKey ?? undefined }))} 
          />          
          <SelectPicker
            label="Type"
            data={RunEventTypeList}
            searchable={false}
            value={filterReq.type ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, type: dataKey ?? undefined }))} 
          />
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
          <Checkbox checked={filterReq.onlyMajors} onChange={(value, checked) => setFilterReq(pre => ({...pre, onlyMajors: checked}))} >Only Majors</Checkbox>
          <Checkbox checked={filterReq.onlyStartedDistances} onChange={(value, checked) => {
            setFilterReq(pre => ({...pre, onlyStartedDistances: checked }));
          }} >Only Started Distances</Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Image</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Type</HeaderCell>
          <Cell dataKey="type">
            {(rowData: RunEventList) => RunEventTypeList.findLast(i => i.value == rowData.type)?.label ?? '-'}
          </Cell>
        </Column>       
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Registration Date</HeaderCell>
          <Cell dataKey="registrationDate">
            {(rowData: RunEventList) => jsDateToZone(rowData.registrationDate , rowData.city.ianaTimeZoneId)}
          </Cell>        
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>City</HeaderCell>
          <Cell dataKey="city.name" />
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Website</HeaderCell>
          <Cell dataKey="website">
          {(rowData: RunEventList)=> rowData.website ? <a href={rowData.website} target='_blank' rel="noopener noreferrer">{rowData.website}</a> : '-'}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Key</HeaderCell>
          <Cell dataKey="runEventKey.name"/>
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Is Major</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: RunEventList) => (   
            <Checkbox checked={rowData.isMajor} readOnly></Checkbox>
          )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" >
          <HeaderCell>Distances</HeaderCell>
          <Cell>
          {(rowData: RunEventList) => (   
            rowData.distances && rowData.distances.length > 0 ? 
            rowData.distances.map((distance, index) => (
              <Tag key={index}>{
                distance?.distance > 0 ? 
                distance?.distance : 
                RunEventDistanceTypeList.findLast(t => t.value == distance.type)?.label}</Tag>
            )) : '-'
          )}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Status</HeaderCell>
          <Cell dataKey="status">
            {(rowData: RunEventList) => RunEventStatusList.findLast(i => i.value == rowData.status)?.label ?? '-'}
          </Cell>
        </Column>       
        <Column flexGrow={0.5} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton onClick={() => nav(`/run-events/${rowData.id}`)} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>
        
      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default RunEventListPage;
